<template>
    <div
        v-if="variant"
        class="rounded-lg bg-slate-50 dark:bg-slate-700/50 px-4 py-3 flex flex-col gap-3"
    >
        <div class="flex items-start gap-2">
            <Icon
                :icon="variant.icon"
                class="w-5 h-5 shrink-0 text-slate-800 dark:text-slate-100"
            />
            <div class="flex flex-col gap-0.5 min-w-0">
                <p
                    class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                >
                    {{ variant.title }}
                </p>
                <p class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400">
                    {{ variant.body }}
                </p>
            </div>
        </div>

        <div class="flex items-center gap-2 flex-wrap">
            <button
                v-for="action in variant.actions"
                :key="action.event"
                type="button"
                class="flex items-center gap-2 px-4 py-2 rounded-[4px] border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150 text-sm font-medium text-slate-800 dark:text-slate-100"
                :disabled="busy"
                @click="$emit(action.event, $event)"
            >
                <Icon :icon="action.icon" class="w-4 h-4" />
                {{ action.label }}
            </button>

            <div v-if="variant.overflow" class="relative" ref="overflowContainer">
                <button
                    type="button"
                    class="flex items-center justify-center w-9 h-9 rounded-[4px] border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                    :disabled="busy"
                    aria-label="More bonus actions"
                    @click="showOverflow = !showOverflow"
                >
                    <Icon icon="dots-vertical" class="w-4 h-4 text-slate-800 dark:text-slate-100" />
                </button>
                <div
                    v-if="showOverflow"
                    class="absolute left-0 bottom-full mb-2 flex p-2 flex-col items-start border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 z-30 min-w-[240px]"
                >
                    <button
                        type="button"
                        class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150 text-sm font-normal text-slate-900 dark:text-slate-100 whitespace-nowrap"
                        @click="onOverflowAction"
                    >
                        <Icon :icon="variant.overflow.icon" class="w-4 h-4" />
                        {{ variant.overflow.label }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import {
    bonusDeadlinePassed,
    useBonusTracking,
} from '@/composables/useBonusTracking';

/**
 * MY1-3043 — the state-dependent action strip under a welcome bonus (or one
 * of its tiers). Three variants:
 *
 *  - goal_reached      → "goal is reached", primary Mark received. The
 *                        missed-goal overflow appears only once the deadline
 *                        has passed (MY1-3074) — see the gate below.
 *  - deadline_passed   → "goal wasn't met", primary confirm-missed, overflow
 *                        Mark received
 *  - untracked overdue → the bonus isn't auto-tracked but its deadline has
 *                        passed; we can't know either way, so both buttons
 *                        sit side by side (design: "Due date has passed.")
 *
 * Every other state renders nothing — marking a still-tracking bonus received
 * lives in the card-details header menu, not the widget.
 */
const props = defineProps({
    // The serialized welcome bonus or tier this strip acts on.
    bonusRow: {
        type: Object,
        required: true,
    },
    busy: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['mark-received', 'goal-not-met']);

const { state } = useBonusTracking(() => props.bonusRow);

const showOverflow = ref(false);
const overflowContainer = ref(null);

const MARK_RECEIVED = {
    event: 'mark-received',
    icon: 'check-lg',
    label: 'Mark received',
};
const GOAL_NOT_MET = {
    event: 'goal-not-met',
    icon: 'circle-x',
    label: "Goal wasn't met, bonus missed",
};

const deadlinePassed = computed(() => bonusDeadlinePassed(props.bonusRow));

const variant = computed(() => {
    const row = props.bonusRow;

    if (!row || row.received || row.goal_not_met) {
        return null;
    }

    if (state.value === 'goal_reached') {
        return {
            icon: 'calendar-check',
            title: 'Spending goal is reached',
            body: 'Bonus can take weeks to arrive. Mark it when it lands.',
            actions: [MARK_RECEIVED],
            // MY1-3074 — resolveState() returns goal_reached the moment spend
            // clears the minimum, deadline or no deadline, while the endpoint
            // (ConnectedAccountService::resolveBonusOutcome) refuses
            // goal_not_met until the deadline is strictly past. Offering it
            // early is an action that can only fail, so it waits.
            overflow: deadlinePassed.value ? { ...GOAL_NOT_MET } : null,
        };
    }

    if (state.value === 'deadline_passed') {
        return {
            icon: 'calendar',
            title: "Looks like the goal wasn't met until due date",
            body: "Confirm bonus wasn't met to dismiss bonus tracking",
            actions: [GOAL_NOT_MET],
            overflow: {
                icon: 'check-lg',
                label: 'Goal reached, bonus received',
                event: 'mark-received',
            },
        };
    }

    // Not auto-tracked (manual issuer, unconnected, or spending off) with the
    // deadline behind us: we cannot know the outcome, so offer both.
    if (
        ['not_auto_trackable', 'connectable', 'spending_disabled'].includes(state.value) &&
        deadlinePassed.value
    ) {
        return {
            icon: 'calendar',
            title: 'Due date has passed. Did you reach the spending goal?',
            body: 'Bonus can take weeks to arrive. Mark it when it lands.',
            actions: [MARK_RECEIVED, { ...GOAL_NOT_MET, label: "Goal wasn't met" }],
            overflow: null,
        };
    }

    return null;
});

const onOverflowAction = (event) => {
    showOverflow.value = false;
    emit(variant.value.overflow.event, event);
};

const onDocumentClick = (event) => {
    if (
        showOverflow.value &&
        overflowContainer.value &&
        !overflowContainer.value.contains(event.target)
    ) {
        showOverflow.value = false;
    }
};

onMounted(() => document.addEventListener('click', onDocumentClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick));
</script>
